import React from 'react';
import { Box, Container, Divider, Typography, Button } from '@mui/material';
import { useParams, Link } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { FacilityIcons } from './components/Accommodations';
import Gallery, { GALLERY_PREVIEW_MODE } from './components/Gallery';
import { usePropertyId } from '../../common/hooks/usePropertyId';
import { useRoomsData } from '../../common/hooks/useRoomsData';
import { useRoomPicturesData } from '../../common/hooks/useRoomPicturesData';
import { useWebsiteContentData } from '../../common/hooks/useWebsiteContentData';
import { withMultipleQueryData } from '../../common/withQueryData';

/**
 * Get room-specific content from accommodations JSON (specs array, each spec may have title, description, longDescription).
 */
const getRoomDetailsContent = (accommodationsData, roomId) => {
  if (!accommodationsData || typeof accommodationsData !== 'object') return null;
  const props = accommodationsData.defaultProps || accommodationsData;
  const specs = Array.isArray(props.specs) ? props.specs : [];
  const spec = specs.find(
    (s) => String(s.id) === String(roomId) || s.id === roomId
  );
  return spec || null;
};

const RoomDetailContent = ({ queryResults }) => {
  const { roomId } = useParams();
  const propertyId = usePropertyId();
  const { queryResults: accommodationsQuery } = useWebsiteContentData(
    propertyId,
    'accommodations'
  );
  const roomData = queryResults.room?.data;
  const roomPicturesAlbum = queryResults.roomPictures?.data;
  const accommodationsData = accommodationsQuery?.data && !accommodationsQuery?.isError
    ? accommodationsQuery.data
    : null;

  const pictureUrls = roomPicturesAlbum?.pictureUrls || [];
  const detailsContent = getRoomDetailsContent(accommodationsData, roomId);
  const longDescription = detailsContent?.longDescription;
  const titleOverride = detailsContent?.title;

  const room = roomData;
  if (!room) {
    return (
      <Container sx={{ py: 4 }}>
        <Typography color="text.secondary">Nie znaleziono obiektu.</Typography>
      </Container>
    );
  }

  const mergedItem = {
    ...detailsContent,
    ...room,
    name: titleOverride || room.name,
  };

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#fafafa', pb: 6 }}>
      <Container sx={{ py: 4 }}>
        <Button
          component={Link}
          to="/"
          startIcon={<ArrowBackIcon />}
          sx={{ mb: 3 }}
        >
          Powrót
        </Button>

        <Box sx={{ maxWidth: 800, mx: 'auto' }}>
          {/* 1. Title */}
          <Typography variant="h4" component="h1" sx={{ fontWeight: 600, mb: 2 }}>
            {mergedItem.name}
          </Typography>

          {/* 2. Gallery */}
          <Box sx={{ mb: 4 }}>
            <Gallery pictureUrls={pictureUrls} previewMode={GALLERY_PREVIEW_MODE.FIXED} />
          </Box>

          {/* 3. Specs + long description */}
          <Divider sx={{ mb: 3 }} />

          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, textTransform: 'uppercase', letterSpacing: '0.08em', fontSize: '0.75rem' }}>
              Wyposażenie
            </Typography>
            <FacilityIcons item={mergedItem} />
          </Box>

          {longDescription && (
            <>
              <Divider sx={{ mb: 3 }} />
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2, textTransform: 'uppercase', letterSpacing: '0.08em', fontSize: '0.75rem' }}>
                O obiekcie
              </Typography>
              {longDescription.split(/\n\n+/).map((paragraph, i) => (
                <Typography key={i} variant="body1" color="text.secondary" paragraph>
                  {paragraph}
                </Typography>
              ))}
            </>
          )}
        </Box>
      </Container>
    </Box>
  );
};

const RoomDetailContentWithQueryData = withMultipleQueryData(RoomDetailContent);

const RoomDetailPage = () => {
  const { roomId } = useParams();
  const propertyId = usePropertyId();

  const { queryResults: roomQueryResults } = useRoomsData(propertyId, roomId);
  const { picturesQueryData } = useRoomPicturesData(propertyId, roomId);

  const queryResults = {
    room: roomQueryResults,
    roomPictures: picturesQueryData,
  };

  return (
    <Box sx={{ position: 'relative', minHeight: '400px', width: '100%' }}>
      <RoomDetailContentWithQueryData queryResults={queryResults} />
    </Box>
  );
};

export default RoomDetailPage;
